import { useState } from 'react';
import track from 'react-tracking';
import {
  COMPANY_VALUE_ANCHOR,
  COMPANY_FUTURE_ANCHOR,
  COMPANY_PAST_ANCHOR,
  COMPANY_HEALTH_ANCHOR,
  COMPANY_DIVIDEND_ANCHOR,
  LegacyAnchor,
  VALUATION_PE_RATIO,
} from '@/constants/anchors';
import { COMPANY_SNOWFLAKE } from '@/constants/feedbackSections';
import { SnowflakeType } from '@/components/Snowflake/SnowflakeType';
import Competitors from '@/components/Competitors';
import type { CompetitorProps } from '@/components/Competitors/Competitor';
import RiskReward from '@/components/RiskReward';
import type { RewardSummaryStatements } from '@/pages/CompanyReport/redux/selectors/getStatements';
import type { SelectedStatement } from '@/pages/CompanyReport/redux/interface';
import {
  SnowflakeWrapper,
  SnowflakeChart,
  RiskRewardWrapper,
  SummaryWrapper,
  ColumnLeft,
  ShowMoreLink,
  SummaryHeading,
  SummaryReportSection,
} from './styled';
import { Mark, SnowflakeIndices } from '@/pages/CompanyReport/redux/interface';
import RiskSummaryList from '@/pages/CompanyReport/components/SummaryCompare/components/RiskSummaryList';
import RiskRewardLoadingSkeleton from '@/pages/CompanyReport/components/SummaryCompare/components/RiskRewardLoadingSkeleton';
import { Button } from '@simplywallst/ui-core';
import { useTracking } from 'react-tracking';
import type { RoutineStatus } from '@ducks/ui';
import Media from 'react-media';
import { useTheme } from '@simplywallst/ui-core';
import Modal from '@/components/Modal';
import loadable from '@loadable/component';
import { SectionTitle } from '@/components/ReportSectionHeader';

const RiskCheckModal = loadable(
  () =>
    import(
      '@/pages/CompanyReport/components/SummaryCompare/components/RiskCheckModal'
    )
);

const Snowflake = loadable(() => import('@/components/Snowflake/Snowflake'), {
  ssr: false,
});

const SnowflakeActions = loadable(
  () => import('@/components/Snowflake/SnowflakeActions')
);
export interface AnalysisChecks {
  [SnowflakeIndices.VALUE]: Mark[];
  [SnowflakeIndices.FUTURE]: Mark[];
  [SnowflakeIndices.PAST]: Mark[];
  [SnowflakeIndices.HEALTH]: Mark[];
  [SnowflakeIndices.DIVIDEND]: Mark[];
}
export interface SummaryCompareProps {
  isFund: boolean;
  companyId: string;
  competitors: CompetitorProps[];
  shortDescription: string;
  description: string;
  companyName: string;
  uniqueSymbol: string;
  tickerSymbol: string;
  sectionNumber: number;
  snowflakePoints: number[];
  fundamentalsSentence?: string;
  analysisChecks: AnalysisChecks;
  rewardsStatements: RewardSummaryStatements;
  hasRewards: boolean;
  summaryRiskStatus: SummaryRiskStatus;
  firstFourFailingRisks: (SelectedStatement & RiskProps)[];
  totalFailingRisks: number;
  fetchStatementsStatus: RoutineStatus;
  allRisks: (SelectedStatement & RiskProps)[];
  onSectionChange?: (section: string) => void;
}

export enum SummaryRiskStatus {
  InsufficientData = 'insufficientData', //All the risks are null or no risk found
  HasPassedAllRisks = 'hasPassedAllRisks', //All possible risks with value===true
  HasNotFailedAnyRisk = 'hasNotFailedAnyRisk', //No risks with value===false (there can be null)
  HasFailedRisks = 'hasFailedRisks', //At least one risk with value===false
}

const routes: string[] = [
  COMPANY_VALUE_ANCHOR,
  COMPANY_FUTURE_ANCHOR,
  COMPANY_PAST_ANCHOR,
  COMPANY_HEALTH_ANCHOR,
  COMPANY_DIVIDEND_ANCHOR,
];

export interface RiskProps {
  name: string;
  hash: string;
  sectionName: string;
}

const SummaryCompare = (props: SummaryCompareProps) => {
  const {
    isFund,
    competitors,
    shortDescription,
    description,
    sectionNumber,
    snowflakePoints,
    fundamentalsSentence,
    uniqueSymbol,
    tickerSymbol,
    hasRewards,
    summaryRiskStatus,
    firstFourFailingRisks,
    allRisks,
    totalFailingRisks,
    fetchStatementsStatus,
    companyName,
    rewardsStatements: {
      isGoodValue,
      isCompanyGrowing,
      hasCompanyBeenGrowing,
      isDividendAttractive,
    },
    analysisChecks,
    onSectionChange,
  } = props;
  const theme = useTheme();
  const [openRisksModal, setOpenRiskModal] = useState(false);
  const [openSummaryModal, setOpenSummaryModal] = useState(false);
  const updateLocationHash = (hash: string) => {
    window.location.hash = hash;
  };
  const tracking = useTracking();
  return (
    <SummaryReportSection
      sectionNumber={sectionNumber}
      data-cy-id="report-section-summary"
    >
      <SummaryWrapper>
        <ColumnLeft col={12} colLg={8}>
          <SectionTitle>Executive Summary</SectionTitle>
          {shortDescription && (
            <p data-cy-id="company-summary-desc">
              <span>{`${shortDescription} `}</span>
              <ShowMoreLink
                data-cy-id="company-summary-more-link"
                as="a"
                styleType="softLink"
                onClick={() => {
                  setOpenSummaryModal(true);
                  tracking.trackEvent({
                    modifier: 'view_business_details',
                    action: 'click',
                  });
                }}
              >
                More Details
              </ShowMoreLink>
            </p>
          )}
          {openSummaryModal && (
            <Modal
              title={uniqueSymbol}
              primaryLabel="Close"
              hideSecondary={true}
              onPrimaryClick={() => {
                setOpenSummaryModal(false);
              }}
              onClose={() => {
                setOpenSummaryModal(false);
              }}
            >
              <>
                <SummaryHeading>Business Profile</SummaryHeading>
                <p>{description}</p>
              </>
            </Modal>
          )}
        </ColumnLeft>
        <RiskRewardWrapper col={12} colLg={8} data-cy-id="risk-reward-wrapper">
          {fetchStatementsStatus === 'success' ? (
            <>
              {hasRewards && (
                <>
                  <h4>Rewards</h4>
                  {!isFund && isGoodValue.value === 'Pass' && (
                    <RiskReward
                      name="isGoodValue"
                      index={0}
                      hash={
                        isGoodValue.outcomeName === 'OUTCOME_TRUE'
                          ? '#' + LegacyAnchor.VALUE_INTRINSIC_VALUE
                          : '#' + VALUATION_PE_RATIO
                      }
                      description={isGoodValue.description}
                    />
                  )}
                  {!isFund && isCompanyGrowing.value === 'Pass' && (
                    <RiskReward
                      name="isCompanyGrowing"
                      index={1}
                      hash={'#' + COMPANY_FUTURE_ANCHOR}
                      description={isCompanyGrowing.description}
                    />
                  )}
                  {!isFund && hasCompanyBeenGrowing.value === 'Pass' && (
                    <RiskReward
                      name="hasCompanyBeenGrowing"
                      index={2}
                      hash={'#' + COMPANY_PAST_ANCHOR}
                      description={hasCompanyBeenGrowing.description}
                    />
                  )}
                  {!isFund && isDividendAttractive.value === 'Pass' && (
                    <RiskReward
                      name="isDividendAttractive"
                      index={3}
                      hash={'#' + COMPANY_DIVIDEND_ANCHOR}
                      description={isDividendAttractive.description}
                    />
                  )}
                </>
              )}
              {!isFund && (
                <>
                  <h4>Risk Analysis</h4>
                  <RiskSummaryList
                    summaryRiskStatus={summaryRiskStatus}
                    firstFourFailingRisks={firstFourFailingRisks}
                    totalFailingRisks={totalFailingRisks}
                    tickerSymbol={tickerSymbol}
                  />
                  <Button
                    onClick={() => setOpenRiskModal(true)}
                    styleType={
                      theme.appTheme === 'light' ? 'gentleReverse' : 'gentle'
                    }
                    data-cy-id="risk-modal-button"
                  >
                    See All Risk Checks
                  </Button>
                </>
              )}
            </>
          ) : (
            <>
              <h4>Rewards</h4>
              <RiskRewardLoadingSkeleton data-cy-id="reward-loading-skeleton" />
              <h4>Risk Analysis</h4>
              <RiskRewardLoadingSkeleton data-cy-id="risk-loading-skeleton" />
            </>
          )}

          {openRisksModal && (
            <RiskCheckModal
              companyShortName={companyName}
              companyTickerSymbol={tickerSymbol}
              riskSummaryList={allRisks}
              onSectionChange={onSectionChange}
              onClose={() => {
                setOpenRiskModal(false);
                tracking.trackEvent({
                  modifier: 'risk_check_modal',
                  action: 'click',
                });
              }}
            />
          )}
        </RiskRewardWrapper>
        <SnowflakeWrapper
          data-cy-id="company-summary-snowflake"
          col={12}
          colLg={4}
        >
          <Media query={{ maxWidth: theme.mdMinRaw }}>
            <hr />
          </Media>
          <h4>Snowflake Analysis</h4>
          <SnowflakeChart>
            <Snowflake
              points={snowflakePoints}
              detailedPoints={analysisChecks}
              tooltip={true}
              routes={routes}
              handleClick={updateLocationHash}
              disabled={isFund}
              type={SnowflakeType.COMPANY}
            />
          </SnowflakeChart>
          {fundamentalsSentence && !isFund && (
            <p data-cy-id="company-fundamentals-desc">{fundamentalsSentence}</p>
          )}
          <SnowflakeActions
            id="comp-summary-snowflake"
            points={snowflakePoints || [0, 0, 0, 0, 0]}
            companyForFeedback={uniqueSymbol}
            feedbackLabel={COMPANY_SNOWFLAKE}
          />
        </SnowflakeWrapper>
      </SummaryWrapper>
      {competitors.length > 0 && <Competitors list={competitors} />}
    </SummaryReportSection>
  );
};

export default track(
  { type: 'track', action: 'landed', subject: 'summary' },
  { dispatchOnMount: true }
)(SummaryCompare) as typeof SummaryCompare;
